import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

function AdminScreen({ onBack }) {
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'user'
  
  // Single Session State
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [selectedCpd, setSelectedCpd] = useState(null);
  const [loading, setLoading] = useState(false);

  // User Analysis State
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userAnalysisData, setUserAnalysisData] = useState(null);
  const [loadingUserAnalysis, setLoadingUserAnalysis] = useState(false);
  const [selectedUserCpd, setSelectedUserCpd] = useState('3.0');
  const [timeRange, setTimeRange] = useState('ALL');

  const getWeibullCurveData = (fit) => {
    if (!fit) return [];
    const data = [];
    for (let x = -4; x <= 0; x += 0.05) {
      const p = 1 - fit.lambda - (1 - fit.gamma - fit.lambda) * Math.exp(-Math.pow(10, fit.beta * (x - fit.alpha)));
      data.push({ x: Number(x.toFixed(2)), p: Number(p.toFixed(3)) });
    }
    return data;
  };

  const getWeibullSummary = () => {
    if (!userAnalysisData || !userAnalysisData.weibull) return { items: [], mean: null };
    
    const items = [];
    let sumAlpha = 0, sumBeta = 0, sumLambda = 0;
    
    Object.keys(userAnalysisData.weibull).forEach(cpd => {
      const fit = userAnalysisData.weibull[cpd].fit;
      if (fit) {
        items.push({ cpd, ...fit });
        sumAlpha += fit.alpha;
        sumBeta += fit.beta;
        sumLambda += fit.lambda;
      }
    });
    
    const count = items.length;
    const mean = count > 0 ? {
      alpha: sumAlpha / count,
      beta: sumBeta / count,
      lambda: sumLambda / count
    } : null;
    
    items.sort((a, b) => parseFloat(a.cpd) - parseFloat(b.cpd));
    return { items, mean };
  };

  useEffect(() => {
    fetch('/api/admin/sessions')
      .then(res => res.json())
      .then(data => setSessions(data))
      .catch(err => console.error("Error fetching sessions:", err));
      
    fetch('/api/users')
      .then(res => res.json())
      .then(data => setUsers(data))
      .catch(err => console.error("Error fetching users:", err));
  }, []);

  useEffect(() => {
    if (selectedSession && activeTab === 'single') {
      setLoading(true);
      fetch(`/api/csf/analyze/${selectedSession}`)
        .then(res => res.json())
        .then(data => {
          setAnalysisData(data);
          const cpds = Object.keys(data);
          if (cpds.length > 0) {
            setSelectedCpd(cpds[0]);
          }
          setLoading(false);
        })
        .catch(err => {
          console.error("Error analyzing session:", err);
          setLoading(false);
        });
    }
  }, [selectedSession, activeTab]);

  const getFilteredHistory = () => {
    if (!userAnalysisData || !userAnalysisData.history) return [];
    
    let filtered = userAnalysisData.history.map((h, i) => {
      const d = new Date(h.created_at);
      return {
        ...h,
        timestamp: d.getTime(),
        date_str: d.toLocaleDateString(),
        index: i + 1
      };
    });
    
    if (timeRange !== 'ALL') {
      const now = new Date().getTime();
      let cutoff = now;
      if (timeRange === '1W') cutoff = now - 7 * 24 * 60 * 60 * 1000;
      if (timeRange === '1M') cutoff = now - 30 * 24 * 60 * 60 * 1000;
      if (timeRange === '6M') cutoff = now - 180 * 24 * 60 * 60 * 1000;
      if (timeRange === '1Y') cutoff = now - 365 * 24 * 60 * 60 * 1000;
      filtered = filtered.filter(h => h.timestamp >= cutoff);
    }
    
    return filtered.sort((a, b) => a.timestamp - b.timestamp);
  };

  useEffect(() => {
    if (activeTab === 'user' && selectedUser) {
      setLoadingUserAnalysis(true);
      fetch(`/api/admin/users/${selectedUser}/analysis?t=${new Date().getTime()}`)
        .then(res => res.json())
        .then(data => {
          setUserAnalysisData(data);
          setLoadingUserAnalysis(false);
        })
        .catch(err => {
          console.error("Error analyzing user:", err);
          setLoadingUserAnalysis(false);
        });
    }
  }, [activeTab, selectedUser]);

  // Format data for Recharts (Single Session)
  const formatChartData = () => {
    if (!analysisData || !selectedCpd || !analysisData[selectedCpd]) return [];
    
    return analysisData[selectedCpd].map(step => {
      const ci_lower = step.threshold_estimate - step.threshold_std;
      const ci_upper = step.threshold_estimate + step.threshold_std;
      
      return {
        trial: step.trial,
        estimate: -step.threshold_estimate,
        ci: [-ci_upper, -ci_lower],
        slope: step.slope_estimate,
        actual_correct: step.response === 1 ? -step.presented_intensity : null,
        actual_incorrect: step.response === 0 ? -step.presented_intensity : null,
      };
    });
  };

  const chartData = formatChartData();

  return (
    <div className="admin-screen" style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto', color: 'white' }}>
      <button onClick={onBack} style={{ marginBottom: '20px', padding: '10px' }}>&larr; Back to Home</button>
      
      <h2>Admin Dashboard</h2>
      
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', borderBottom: '1px solid #444', paddingBottom: '10px' }}>
        <button 
          onClick={() => setActiveTab('single')}
          style={{ padding: '10px 20px', background: activeTab === 'single' ? '#4dabf7' : '#333', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          Single Session Replay
        </button>
        <button 
          onClick={() => setActiveTab('user')}
          style={{ padding: '10px 20px', background: activeTab === 'user' ? '#4dabf7' : '#333', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          User Analysis
        </button>
      </div>

      {activeTab === 'single' && (
        <>
          <div className="admin-controls" style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
            <div>
              <label>Select Session: </label>
              <select 
                value={selectedSession || ''} 
                onChange={(e) => setSelectedSession(e.target.value)}
                style={{ padding: '5px' }}
              >
                <option value="">-- Choose a Session --</option>
                {sessions.map(s => (
                  <option key={s.id} value={s.session_uuid}>
                    {s.user_name} - {new Date(s.created_at).toLocaleString()} ({s.test_type})
                  </option>
                ))}
              </select>
            </div>

            {analysisData && (
              <div>
                <label>Select CPD: </label>
                <select 
                  value={selectedCpd || ''} 
                  onChange={(e) => setSelectedCpd(e.target.value)}
                  style={{ padding: '5px' }}
                >
                  {Object.keys(analysisData).map(cpd => (
                    <option key={cpd} value={cpd}>{cpd} cpd</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {loading && <p>Analyzing history...</p>}

          {!loading && chartData.length > 0 && (
            <div className="charts-container">
              
              <div className="chart-wrapper" style={{ marginBottom: '40px' }}>
                <h3>Threshold (logCS) Evolution - {selectedCpd} cpd</h3>
                <p style={{ fontSize: '12px', color: '#ccc' }}>
                  Line = Estimated Threshold. Shaded Area = ±1 Std Dev. Green = Correct, Red = Incorrect.
                </p>
                <div style={{ height: '400px', backgroundColor: '#1e1e1e', padding: '20px', borderRadius: '8px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                      <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                      <XAxis dataKey="trial" type="number" domain={['dataMin', 'dataMax']} tickCount={chartData.length} stroke="#ccc" />
                      <YAxis domain={[-3, 0]} stroke="#ccc" tickFormatter={(val) => val.toFixed(2)} label={{ value: 'logCS (Contrast)', angle: -90, position: 'insideLeft', fill: '#ccc' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#333', color: '#fff', border: 'none' }} />
                      <Legend />
                      <Area type="monotone" dataKey="ci" stroke="none" fill="#4dabf7" fillOpacity={0.2} name="±1 Std Dev" />
                      <Line type="monotone" dataKey="estimate" stroke="#4dabf7" strokeWidth={3} name="Estimated logCS" dot={false} />
                      <Scatter dataKey="actual_correct" fill="#40c057" name="Correct Trial" shape="circle" />
                      <Scatter dataKey="actual_incorrect" fill="#fa5252" name="Incorrect Trial" shape="cross" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="chart-wrapper">
                <h3>Slope Evolution - {selectedCpd} cpd</h3>
                <div style={{ height: '300px', backgroundColor: '#1e1e1e', padding: '20px', borderRadius: '8px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                      <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                      <XAxis dataKey="trial" type="number" domain={['dataMin', 'dataMax']} tickCount={chartData.length} stroke="#ccc" />
                      <YAxis domain={[2, 5]} stroke="#ccc" tickFormatter={(val) => val.toFixed(2)} label={{ value: 'Slope', angle: -90, position: 'insideLeft', fill: '#ccc' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#333', color: '#fff', border: 'none' }} />
                      <Legend />
                      <Line type="stepAfter" dataKey="slope" stroke="#fcc419" strokeWidth={3} name="Estimated Slope" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

            </div>
          )}

          {selectedSession && (
            <div className="chart-wrapper" style={{ marginTop: '40px' }}>
              <h3>Reaction Times (Overall Session)</h3>
              {sessions.find(s => s.session_uuid === selectedSession)?.session_metadata?.timing?.reaction_times_sec?.length > 0 ? (
                <div style={{ height: '300px', backgroundColor: '#1e1e1e', padding: '20px', borderRadius: '8px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart 
                      data={sessions.find(s => s.session_uuid === selectedSession).session_metadata.timing.reaction_times_sec.map((time, idx) => {
                        const sessionObj = sessions.find(s => s.session_uuid === selectedSession);
                        const correctArray = sessionObj.session_metadata.timing.reaction_correct;
                        const isCorrect = correctArray ? correctArray[idx] : null;
                        return { 
                          trial: idx + 1, 
                          time: time,
                          time_correct: isCorrect === true ? time : null,
                          time_incorrect: isCorrect === false ? time : null,
                          time_unknown: isCorrect === null ? time : null
                        };
                      })} 
                      margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                    >
                      <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                      <XAxis dataKey="trial" type="number" domain={['dataMin', 'dataMax']} stroke="#ccc" />
                      <YAxis domain={[0, 'auto']} stroke="#ccc" tickFormatter={(val) => val.toFixed(1)} label={{ value: 'Seconds', angle: -90, position: 'insideLeft', fill: '#ccc' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#333', color: '#fff', border: 'none' }} />
                      <Legend />
                      <Line type="monotone" dataKey="time" stroke="#c2255c" strokeWidth={2} name="Trend" dot={false} />
                      <Scatter dataKey="time_correct" fill="#40c057" name="Correct" shape="circle" />
                      <Scatter dataKey="time_incorrect" fill="#fa5252" name="Incorrect" shape="cross" />
                      <Scatter dataKey="time_unknown" fill="#c2255c" name="Unknown" shape="circle" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p style={{ color: '#888', fontStyle: 'italic' }}>No timing data available for this legacy session.</p>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'user' && (
        <>
          <div className="admin-controls" style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
            <div>
              <label>Select User: </label>
              <select 
                value={selectedUser || ''} 
                onChange={(e) => setSelectedUser(e.target.value)}
                style={{ padding: '5px' }}
              >
                <option value="">-- Choose a User --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>

          {loadingUserAnalysis && <p>Loading user data...</p>}

          {!loadingUserAnalysis && userAnalysisData && (
            <div>
              <div className="chart-wrapper" style={{ marginBottom: '40px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3>AULCSF History</h3>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {['1W', '1M', '6M', '1Y', 'ALL'].map(range => (
                      <button 
                        key={range}
                        onClick={() => setTimeRange(range)}
                        style={{
                          background: timeRange === range ? '#4dabf7' : '#333',
                          color: '#fff',
                          border: 'none',
                          padding: '5px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ height: '300px', backgroundColor: '#1e1e1e', padding: '20px', borderRadius: '8px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart 
                      data={getFilteredHistory()} 
                      margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                    >
                      <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="timestamp" 
                        type="number" 
                        scale="time"
                        domain={['dataMin', 'dataMax']} 
                        tickFormatter={(unixTime) => new Date(unixTime).toLocaleDateString([], {day: '2-digit', month: '2-digit'})}
                        stroke="#ccc" 
                        minTickGap={30}
                      />
                      <YAxis domain={['auto', 'auto']} stroke="#ccc" label={{ value: 'AULCSF', angle: -90, position: 'insideLeft', fill: '#ccc' }} />
                      <Tooltip 
                        labelFormatter={(label) => new Date(label).toLocaleString([], {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute:'2-digit'})}
                        contentStyle={{ backgroundColor: '#333', color: '#fff', border: 'none' }} 
                      />
                      <Legend />
                      <Line type="monotone" dataKey="aulcsf" stroke="#4dabf7" strokeWidth={3} name="AULCSF Area" dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="chart-wrapper">
                <h3>Test-Retest Variability (TRV) per CPD</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', backgroundColor: '#1e1e1e', borderRadius: '8px', overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#333', textAlign: 'left' }}>
                      <th style={{ padding: '15px' }}>CPD</th>
                      <th style={{ padding: '15px' }}>N (Sessions)</th>
                      <th style={{ padding: '15px' }}>Mean (logCS)</th>
                      <th style={{ padding: '15px' }}>Std Dev</th>
                      <th style={{ padding: '15px' }}>95% TRV (1.96 * SD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userAnalysisData.cpd_stats.map((stat, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #333' }}>
                        <td style={{ padding: '15px' }}>{stat.cpd}</td>
                        <td style={{ padding: '15px' }}>{stat.n}</td>
                        <td style={{ padding: '15px' }}>{stat.mean.toFixed(2)}</td>
                        <td style={{ padding: '15px' }}>{stat.std.toFixed(2)}</td>
                        <td style={{ padding: '15px', color: '#4dabf7', fontWeight: 'bold' }}>± {stat.trv_95.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="chart-wrapper" style={{ marginTop: '40px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>Aggregated Psychometric Function (Weibull Fit)</h3>
                  <div>
                    <label>Select CPD: </label>
                    <select 
                      value={selectedUserCpd} 
                      onChange={(e) => setSelectedUserCpd(e.target.value)}
                      style={{ padding: '5px' }}
                    >
                      {userAnalysisData.cpd_stats.filter(s => s.cpd !== 'Σ AULCSF').map(s => (
                        <option key={s.cpd} value={s.cpd}>{s.cpd} CPD</option>
                      ))}
                    </select>
                  </div>
                </div>

                {userAnalysisData.weibull && userAnalysisData.weibull[selectedUserCpd] && (
                  <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
                    <div style={{ flex: 2, height: '400px', backgroundColor: '#1e1e1e', padding: '20px', borderRadius: '8px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                          <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                          <XAxis type="number" dataKey="x" name="logCS" domain={[-3, 0]} stroke="#ccc" tickCount={7} label={{ value: 'logCS (Contrast)', position: 'insideBottom', offset: -10, fill: '#ccc' }} />
                          <YAxis type="number" dataKey="p" name="Probability" domain={[0, 1.05]} stroke="#ccc" label={{ value: 'P(Correct)', angle: -90, position: 'insideLeft', fill: '#ccc' }} />
                          <Tooltip contentStyle={{ backgroundColor: '#333', color: '#fff', border: 'none' }} />
                          <Legend />
                          
                          {/* Curve */}
                          {userAnalysisData.weibull[selectedUserCpd].fit && (
                            <Line 
                              type="monotone" 
                              data={getWeibullCurveData(userAnalysisData.weibull[selectedUserCpd].fit)} 
                              dataKey="p" 
                              stroke="#fcc419" 
                              strokeWidth={3} 
                              dot={false} 
                              name="Weibull Fit" 
                              isAnimationActive={false}
                            />
                          )}

                          {/* Raw Data */}
                          <Scatter 
                            name="Raw Answers" 
                            data={userAnalysisData.weibull[selectedUserCpd].raw.map((d, i) => ({ id: i, x: d.x, p: d.y }))} 
                            fill="#8884d8" 
                            shape={(props) => {
                              const { cx, cy, payload } = props;
                              return <circle cx={cx} cy={cy} r={4} fill={payload.p === 1 ? "#40c057" : "#fa5252"} opacity={0.6} />;
                            }} 
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>

                    <div style={{ flex: 1, backgroundColor: '#1e1e1e', padding: '20px', borderRadius: '8px' }}>
                      <h4>Fit Parameters (95% CI)</h4>
                      {userAnalysisData.weibull[selectedUserCpd].fit ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                          <tbody>
                            <tr style={{ borderBottom: '1px solid #333' }}>
                              <td style={{ padding: '10px' }}><strong>Threshold (α)</strong></td>
                              <td style={{ padding: '10px', textAlign: 'right' }}>{userAnalysisData.weibull[selectedUserCpd].fit.alpha.toFixed(2)} ± {userAnalysisData.weibull[selectedUserCpd].fit.alpha_ci.toFixed(2)}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #333' }}>
                              <td style={{ padding: '10px' }}><strong>Slope (β)</strong></td>
                              <td style={{ padding: '10px', textAlign: 'right' }}>{userAnalysisData.weibull[selectedUserCpd].fit.beta.toFixed(2)} ± {userAnalysisData.weibull[selectedUserCpd].fit.beta_ci.toFixed(2)}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #333' }}>
                              <td style={{ padding: '10px' }}><strong>Lapse Rate (λ)</strong></td>
                              <td style={{ padding: '10px', textAlign: 'right' }}>{userAnalysisData.weibull[selectedUserCpd].fit.lambda.toFixed(3)} ± {userAnalysisData.weibull[selectedUserCpd].fit.lambda_ci.toFixed(3)}</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '10px' }}><strong>Guess Rate (γ)</strong></td>
                              <td style={{ padding: '10px', textAlign: 'right' }}>0.250 (Fixed)</td>
                            </tr>
                          </tbody>
                        </table>
                      ) : (
                        <p style={{ color: '#888', marginTop: '20px' }}>Not enough data points for a stable Weibull curve fit (N &gt; 10 required).</p>
                      )}
                    </div>
                  </div>
                )}

                {/* New Summary Table */}
                {(() => {
                  const summary = getWeibullSummary();
                  if (summary.items.length === 0) return null;
                  return (
                    <div style={{ marginTop: '40px' }}>
                      <h3>Weibull Fit Parameters Summary</h3>
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', backgroundColor: '#1e1e1e', borderRadius: '8px', overflow: 'hidden' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#333', textAlign: 'left' }}>
                            <th style={{ padding: '15px' }}>CPD</th>
                            <th style={{ padding: '15px' }}>Threshold (α)</th>
                            <th style={{ padding: '15px' }}>Slope (β)</th>
                            <th style={{ padding: '15px' }}>Lapse Rate (λ)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.items.map((item, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #333' }}>
                              <td style={{ padding: '15px' }}>{item.cpd}</td>
                              <td style={{ padding: '15px', color: '#4dabf7' }}>{item.alpha.toFixed(2)} <span style={{color: '#888', fontSize: '0.9em'}}>± {item.alpha_ci.toFixed(2)}</span></td>
                              <td style={{ padding: '15px', color: '#4dabf7' }}>{item.beta.toFixed(2)} <span style={{color: '#888', fontSize: '0.9em'}}>± {item.beta_ci.toFixed(2)}</span></td>
                              <td style={{ padding: '15px', color: '#4dabf7' }}>{item.lambda.toFixed(3)} <span style={{color: '#888', fontSize: '0.9em'}}>± {item.lambda_ci.toFixed(3)}</span></td>
                            </tr>
                          ))}
                          {summary.mean && (
                            <tr style={{ backgroundColor: '#2b2b2b', fontWeight: 'bold' }}>
                              <td style={{ padding: '15px', borderTop: '2px solid #555' }}>Mean</td>
                              <td style={{ padding: '15px', borderTop: '2px solid #555' }}>{summary.mean.alpha.toFixed(2)}</td>
                              <td style={{ padding: '15px', borderTop: '2px solid #555' }}>{summary.mean.beta.toFixed(2)}</td>
                              <td style={{ padding: '15px', borderTop: '2px solid #555' }}>{summary.mean.lambda.toFixed(3)}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
}

export default AdminScreen;
